import {
    getWebsiteAnalytics_api,
    getWebsiteCollectedData_api,
    getWebsiteTourData_api,
    WebsiteBuilderData,
} from 'api/websiteBuilderApi';
import { WebsiteAnalyticsData } from 'modules/Types';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import useBrand from './useBrand';

interface WebsiteBuilderAnalyticsStore {
    // State
    websiteAnalyticsData: WebsiteAnalyticsData | null;
    websiteCollectedData: any | null;
    websiteTourData: any | null;
    isLoadingWebsiteAnalyticsData: boolean;
    isLoadingWebsiteCollectedData: boolean;
    isLoadingWebsiteTourData: boolean;

    // Actions
    setWebsiteAnalyticsData: (websiteAnalyticsData: WebsiteAnalyticsData) => void;
    setWebsiteCollectedData: (websiteCollectedData: any) => void;
    setWebsiteTourData: (websiteTourData: any) => void;
    setIsLoadingWebsiteAnalyticsData: (isLoadingWebsiteAnalyticsData: boolean) => void;
    setIsLoadingWebsiteCollectedData: (isLoadingWebsiteCollectedData: boolean) => void;
    setIsLoadingWebsiteTourData: (isLoadingWebsiteTourData: boolean) => void;

    // Methods
    getWebsiteAnalytics: ({ websiteId, startDate, endDate, mock }: { websiteId: number, startDate: string, endDate: string, mock?: boolean }) => Promise<WebsiteBuilderData | undefined>;
    getWebsiteCollectedData: ({ websiteId }: { websiteId: number }) => Promise<any | undefined>;
    getWebsiteTourData: ({ websiteId }: { websiteId: number }) => Promise<any | undefined>;
}

const useWebsiteBuilderAnalytics = create(immer<WebsiteBuilderAnalyticsStore>((set, get) => ({
    // State
    websiteAnalyticsData: null,
    websiteCollectedData: null,
    websiteTourData: null,
    isLoadingWebsiteAnalyticsData: false,
    isLoadingWebsiteCollectedData: false,
    isLoadingWebsiteTourData: false,

    // Actions
    setWebsiteAnalyticsData: (websiteAnalyticsData: WebsiteAnalyticsData) => set({ websiteAnalyticsData }),
    setWebsiteCollectedData: (websiteCollectedData: any) => set({ websiteCollectedData }),
    setWebsiteTourData: (websiteTourData: any) => set({ websiteTourData }),
    setIsLoadingWebsiteAnalyticsData: (isLoadingWebsiteAnalyticsData: boolean) => set({ isLoadingWebsiteAnalyticsData }),
    setIsLoadingWebsiteCollectedData: (isLoadingWebsiteCollectedData: boolean) => set({ isLoadingWebsiteCollectedData }),
    setIsLoadingWebsiteTourData: (isLoadingWebsiteTourData: boolean) => set({ isLoadingWebsiteTourData }),

    // Methods
    getWebsiteAnalytics: async ({ websiteId, startDate, endDate, mock }: { websiteId: number, startDate: string, endDate: string, mock?: boolean }) => {
        const { isPaidBrand } = useBrand.getState();
        
        // Paywall: Prevent API calls for free users to reduce costs
        if (!isPaidBrand) {
            set({ websiteAnalyticsData: null });
            return undefined;
        }

        set({ isLoadingWebsiteAnalyticsData: true });
        try {
            const res = await getWebsiteAnalytics_api({ websiteId, startDate, endDate, mock });

            set({ websiteAnalyticsData: res as unknown as WebsiteAnalyticsData });
            return res;
        } catch (error) {
            console.error(error);
        } finally {
            set({ isLoadingWebsiteAnalyticsData: false });
        }
    },
    getWebsiteCollectedData: async ({ websiteId }: { websiteId: number }) => {
        const { isPaidBrand } = useBrand.getState();
        
        // Paywall: Prevent API calls for free users to reduce costs
        if (!isPaidBrand) {
            set({ websiteCollectedData: null });
            return undefined;
        }

        set({ isLoadingWebsiteCollectedData: true });
        try {
            const res = await getWebsiteCollectedData_api({ websiteId });
            set({ websiteCollectedData: res });
            return res;
        } catch (error) {
            console.error(error);
        } finally {
            set({ isLoadingWebsiteCollectedData: false });
        }
    },
    getWebsiteTourData: async ({ websiteId }: { websiteId: number }) => {
        const { isPaidBrand } = useBrand.getState();
        
        // Paywall: Prevent API calls for free users to reduce costs  
        if (!isPaidBrand) {
            set({ websiteTourData: null });
            return undefined;
        }

        set({ isLoadingWebsiteTourData: true });
        try {
            const res = await getWebsiteTourData_api({ websiteId });
            set({ websiteTourData: res });
            return res;
        } catch (error) {
            console.error(error);
        } finally {
            set({ isLoadingWebsiteTourData: false });
        }
    }
}))
);

export default useWebsiteBuilderAnalytics;
